import {createFocusTrap, FocusTrap} from 'focus-trap';
import {
    IVanillaReaderNavigationItem,
    IVanillaReaderNavigationTreeData,
    VanillaMenuHiddenCallback,
    VanillaReaderNavigationType,
    VanillaStringValueCallback,
    VanillaVoidCallback
} from '../VanillaReader/VanillaReaderModel';
import { NavigationCollectionType } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { TreeView, ITreeViewItemSourceData } from '../utils/TreeView';
import { VanillaUiNavigationIntentEventCallback } from './VanillaReaderUiModel';

/**
 * # VanillaReaderUiSideMenu
 *
 * ## RESPONSIBILITIES
 *
 * ## RELATED TYPES
 * - VanillaReaderUiSideMenuController
 *
 */

export class VanillaReaderUiSideMenu {
    private _menu: HTMLElement;
    private _buttonMenuHandle: HTMLElement;
    private _publicationTitle: HTMLElement;
    private _dataListActions: HTMLDataListElement;
    private _sectionToc: HTMLElement;

    private _treeViewToc: TreeView | undefined = undefined;
    private _treeViewTocItems: ITreeViewItemSourceData[] | undefined;
    private _tocItemsListFlat: ITreeViewItemSourceData[] | undefined;

    private _searchForm: HTMLFormElement;
    private _searchInput: HTMLInputElement;

    private _hostDocument: Document;

    private _touchEventCoords: number[] | undefined;

    private _focusTrap: FocusTrap | undefined;

    private _menuHandleClickedCallback: VanillaVoidCallback | undefined;
    private _swipeDownCallback: VanillaVoidCallback | undefined;
    private _visibleCallback: VanillaVoidCallback | undefined;
    private _hiddenCallback: VanillaMenuHiddenCallback | undefined;
    private _navItemClickedCallback: VanillaUiNavigationIntentEventCallback | undefined = undefined;
    private _onExecuteSearchCallback: VanillaStringValueCallback | undefined = undefined;
    private _onClearSearchCallback: VanillaVoidCallback | undefined = undefined;

    private _style = `
        <style>
            #vanilla-reader__side-menu {                
                position: fixed;
                left: 0;
                opacity: 0;
                display: flex;  
                width: 35vw !important;  
                height:100%;              
                margin-left: auto;
                margin-right: auto;
                flex-direction: row;
                flex-wrap: wrap;
                box-sizing: border-box;
                z-index: 2;
                background-color: var(--vanilla-reader__color__ui__background-light);                
                box-shadow: 0 -3px 16px 0 rgb(0 0 0 / 30%);
                border-radius: 16px 16px 0 0;
                border: none;
                border-top: 1px solid var(--vanilla-reader__color__ui__outline-dark);
                pointer-events: none;                
                transition: left 0.2s ease-out;
                will-change: left;
                overflow: auto;
            }
                    
            @media (prefers-reduced-motion: reduce) {
                #vanilla-reader__side-menu {
                    transition: opacity 0.2s ease-out;
                }
            }
            
            #vanilla-reader__side-menu button {
                flex-direction: column;                
                min-height: 48px;
                min-width: 48px;
                font-size: x-large;
                color: var(--vanilla-reader__color__ui__foreground-dark);
                border-color: transparent;
                background-color: transparent;                
            }           
            

      
            #vanilla-reader__side-menu__button-handle {
                height: 24px;                
                min-height: 24px!important;
                width: 100%;
                margin: 8px;
                align-self: center;                
                background-color: transparent;               
                border: 0;
            }

            #vanilla-reader__side-menu__button-handle span {
                width: 100%;
                display: block;
                font-size: x-large;
                color: var(--vanilla-reader__color__ui__foreground-dark);
            }

            #vanilla-reader__side-menu__top-card {
                position:relative;
                background-color: #4d9056!important;
                border-color: #4d9056!important;
                overflow:hidden;
                min-height:150px;
                display: flex;
                align-items:center;
                width: 100%;
            }

            #vanilla-reader__side-menu__top-card:after {
                background: url(data:image/svg+xml;base64,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);
                background-position: 50%;
                background-size: cover;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                right: 0;
                opacity: .3;
                content: "";
                background-repeat: no-repeat;
            }

            #vanilla-reader__side-menu__publication-title {
                font-size:1.5rem;
                font-weight:bold;
            }

            #vanilla-reader___side-menu__search-form__input {
                width: 100%;
            }

        </style>`;

    private _template = `
        <aside id="vanilla-reader__side-menu" title="Menu section" aria-hidden="true" inert>     
            <div id="vanilla-reader__side-menu__top-card">
                <div style="z-index:1000; position:absolute; display:flex; width:100%; ">
                    <button id="vanilla-reader__side-menu__button-handle" title="Close menu" accesskey="x" style="flex:0"><span aria-hidden="true">chevron_left</span></button>
                    <div style="flex:1; display:flex; align-items:center;"><span id="vanilla-reader__side-menu__publication-title"></span></div>
                </div>
            </div>
            <div style="width:100%; padding: 0 20px">
                <form id="vanilla-reader___side-menu__search-form">
                    <input type="search" placeholder="Enter search term" aria-label="Enter search term" id="vanilla-reader___side-menu__search-form__input">
                </form>
            </div>
            <hr />
            <section id="vanilla-reader__side-men__section-toc">
                <datalist id="vanilla-reader__side-menu__datalist"></datalist>
            </section>
        </aside>`;

    constructor(
        private _hostElement: HTMLElement,
        insertPosition: InsertPosition = 'beforeend'
    ) {

        this._hostElement.insertAdjacentHTML(insertPosition, this._template);
        this._hostDocument = _hostElement.ownerDocument!;

        this._menu = this._hostDocument.getElementById('vanilla-reader__side-menu')!;
        this._menu.insertAdjacentHTML('afterbegin', this._style);
        this._buttonMenuHandle = this._hostDocument.getElementById('vanilla-reader__side-menu__button-handle')!;

        this._menu.addEventListener('keyup', this._event_menu_keyup);

        this._menu.inert = true;

        this._sectionToc = this._menu.querySelector<HTMLElement>('#vanilla-reader__side-men__section-toc')!;
        this._publicationTitle = this._hostDocument.getElementById("vanilla-reader__side-menu__publication-title")!;

        this._searchForm = this._hostDocument.getElementById('vanilla-reader___side-menu__search-form')! as HTMLFormElement;
        this._searchInput = this._hostDocument.getElementById('vanilla-reader___side-menu__search-form__input')! as HTMLInputElement;

        this._searchForm.addEventListener('submit', this._event_formSubmit);
        this._searchInput.addEventListener('input', this._event_formMaybeClear);

        this._dataListActions = this._menu.querySelector<HTMLDataListElement>('#vanilla-reader__side-menu__datalist')!;

        // Ok, we have references to all the menu component's HTML elements. Now let's setup various behaviours such as
        // event listeners.
        this._setup();

    }

    getComponentElement(): HTMLElement {
        return this._hostDocument.getElementById('vanilla-reader__side-menu')!;
    }

    onMenuHandleSwipeDown(callback: VanillaVoidCallback): void {
        this._swipeDownCallback = callback;
    }

    onMenuHandleClicked(callback: VanillaVoidCallback): void {
        this._menuHandleClickedCallback = callback;
    }

    onMenuVisible(callback: VanillaVoidCallback): void {
        this._visibleCallback = callback;
    }

    onMenuHidden(callback: VanillaMenuHiddenCallback): void {
        this._hiddenCallback = callback;
    }

    onNavItemClicked(callback: VanillaUiNavigationIntentEventCallback): void {
        this._navItemClickedCallback = callback;
    }

    onSearchExecute(callback: VanillaStringValueCallback): void {
        this._onExecuteSearchCallback = callback;
    }

    onSearchClear(callback: VanillaVoidCallback): void {
        this._onClearSearchCallback = callback;
    }

    setAsImportantForAccessibility(): void {
        this._menu.removeAttribute('aria-hidden');
    }

    setAsNotImportantForAccessibility(): void {
        this._menu.setAttribute('aria-hidden', 'true');
    }

    setPublicationTitle(title: string): void {
        this._publicationTitle.innerHTML = title;
    }


    public get isVisible(): boolean {
        return this._menu.style.opacity === '1';
    }

    show(animate: boolean = true): void {
        if (!animate) {
            this._menu.style.transition = 'opacity 0.2s ease-out';
        } else {
            this._menu.style.transition = 'left 0.2s ease-out';
        }

        this._toggleMenuButtonsKeyboardFocusable(true);

        this._menu.tabIndex = 0;
        this._menu.inert = false;
        this._menu.style.opacity = '1';
        this._menu.style.left = '0';

        this._focusTrap = createFocusTrap(this._menu, {
            allowOutsideClick: true,
            escapeDeactivates: false,
            returnFocusOnDeactivate: true,
        });
        this._focusTrap.activate();

        // Wait until just a bit before turning on events and calling the callback function.
        window.setTimeout(() => {
            this._menu.style.pointerEvents = 'auto';

            if (this._visibleCallback) {
                this._visibleCallback();
            }

        }, 500);

    }

    hide(animate: boolean = true, silent: boolean = false): void {
        if (!animate) {
            this._menu.style.transition = 'opacity 0.2s ease-out';
        } else {
            this._menu.style.transition = 'left 0.2s ease-out';
        }

        this._menu.tabIndex = -1;
        this._toggleMenuButtonsKeyboardFocusable(false);
        this._menu.inert = true;

        this._menu.style.pointerEvents = 'none';
        this._menu.style.left = `${-(this._menu.clientWidth + 16)}px`;

        // Wait for the animation to finish before calling callback method etc.
        window.setTimeout(() => {
            this._menu.style.opacity = '0';

            if (this._focusTrap) {

                let options: { returnFocus: boolean } = {
                    returnFocus: false,
                };
                try {
                    this._focusTrap.deactivate(options);
                } catch (e) {

                }
            }

            if (this._hiddenCallback) {
                this._hiddenCallback( 'menu', silent);
            }
        }, 400);
    }


    populateContents(treeData: IVanillaReaderNavigationTreeData): void {
        if (treeData) {

            let collections = treeData.collections;
            if (collections) {

                this.empty();

                for (const collection of collections) {

                    if (collection.type === NavigationCollectionType.TOC) {
                        if (!this._treeViewTocItems) {
                            this._treeViewTocItems = this._mapNavigationItemDataToTreeItemData(collection.children);
                        }

                        this._tocItemsListFlat = this._treeViewTocItems.flatMap((item)=>{
                            return [item ,...item.children]
                        });

                        for(let item of this._tocItemsListFlat) {
                            if(item.data) {
                                let optionElement = document.createElement('option');
                                optionElement.text = item.textContent;
                                optionElement.setAttribute('data-action-id', item.id);
                                optionElement.setAttribute('data-action-name', item.data?.name || '');
                                this._dataListActions.appendChild(optionElement);
                            }
                        }

                        this._treeViewToc = new TreeView<IVanillaReaderNavigationItem>(this._sectionToc, this._treeViewTocItems);
                        this._treeViewToc.cssClassName = 'vanilla-reader__dialog-contents__list-toc';

                        this._treeViewToc.addItemClickEventHandler((navItem: ITreeViewItemSourceData<IVanillaReaderNavigationItem>) => {

                            if (navItem.data.locator && this._navItemClickedCallback) {
                                this._navItemClickedCallback({
                                    navigationType: VanillaReaderNavigationType.GOTO,
                                    locator: navItem.data.locator,
                                });
                            }

                        });
                        this._treeViewToc.render();
                    }

                }
            }
        }
    }

    empty(): void {
        if (this._treeViewToc) {
            this._treeViewToc.destroy();
        }
    }

    clear(): void {
        this._searchInput.value = '';
        this._searchInput.focus();
        if (this._onClearSearchCallback) {
            this._onClearSearchCallback();
        }
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private _setup(): void {

        this.hide();

        this._buttonMenuHandle.addEventListener('click', (_ev) => {
            if (this._menuHandleClickedCallback) {
                this._menuHandleClickedCallback();
            }
        });

        this._buttonMenuHandle.addEventListener('touchstart', (ev) => {
            this._touchEventCoords = [ev.changedTouches[0].clientX, ev.changedTouches[0].clientY];
        });

        this._buttonMenuHandle.addEventListener('touchend', (ev) => {
            if (this._touchEventCoords && this._touchEventCoords[1] - ev.changedTouches[0].clientY <= -96) {
                if (this._swipeDownCallback) {
                    this._swipeDownCallback();
                }
            }
        });

    }

    private _toggleMenuButtonsKeyboardFocusable(focusable: boolean): void {
        let tabIndex = 0;
        if(!focusable) {
            tabIndex = -1;
        }
        this._menu.querySelectorAll('button').forEach(button => button.tabIndex = tabIndex);
    }

    private _mapNavigationItemDataToTreeItemData(navigationItems: IVanillaReaderNavigationItem[]): ITreeViewItemSourceData<IVanillaReaderNavigationItem>[] {
        const map = (navigationItem: IVanillaReaderNavigationItem): ITreeViewItemSourceData => {
            return {
                data: navigationItem,
                id: navigationItem.locator || '',
                textContent: navigationItem.title,
                children: navigationItem.children && navigationItem.children.map(map),
            };
        };

        return navigationItems.map(map);
    }

    /*
    *
    * EVENT HANDLERS
    *
    * */

    private _event_menu_keyup = (ev: KeyboardEvent) => {
        switch (ev.key) {
            case 'Escape':
                this.hide();
                break;
        }
    };

    private _event_formSubmit = (ev: SubmitEvent) => {
        ev.preventDefault();

        if (this._onExecuteSearchCallback) {
            this._onExecuteSearchCallback(this._searchInput.value);
        }
    };

    private _event_formMaybeClear = (_ev: Event) => {
        if (this._searchInput.value === '' && this._onClearSearchCallback) {
            this.clear();
        }
    };
}
